import React, { useState, useEffect, useCallback } from 'react';
import { SOP } from '../../types/sop';
import { getDepartmentColorClass } from '../../utils/departmentColors';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Star, Plus, FolderEdit, MoveRight, Globe } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import favoritesService from '@/services/favoritesService';
import CreatorCategoryView from '@/components/sop/CreatorCategoryView';
import ImportedCategoryView from '@/components/sop/ImportedCategoryView';
import CollapsibleCategory from '@/components/sop/CollapsibleCategory';
import { useCategories } from '@/context/CategoryContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from '../../components/ui/use-toast';
import PublicCategoriesList from '../../components/sop/PublicCategoriesList';
import '../../styles/sop-favorites-override.css';

// Import Category type
import { Category } from '@/types/category';

interface FavoriteSOPsProps {}

const FavoriteSOPs: React.FC<FavoriteSOPsProps> = () => {
  const { user, displayName, profilePictureUrl } = useAuth();
  const { categories, refreshCategories } = useCategories();
  const [sops, setSops] = useState<{ [key: string]: SOP }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [selectedSOP, setSelectedSOP] = useState<SOP | null>(null);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('my-favorites');

  const loadFavorites = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      // Load SOPs for these categories
      const allSopIds = new Set<string>();
      categories.forEach(category => {
        category.sopIds.forEach(id => allSopIds.add(id));
      });
      
      // Fetch SOPs data if needed
      if (allSopIds.size > 0) {
        console.log('Fetching SOPs with IDs:', Array.from(allSopIds));
        const sopsData: { [key: string]: SOP } = {};

        // Fetch each SOP individually
        await Promise.all(Array.from(allSopIds).map(async (sopId) => {
          try {
            const sopDoc = await getDoc(doc(db, 'sops', sopId));
            if (sopDoc.exists()) {
              sopsData[sopId] = sopDoc.data() as SOP;
            } else {
              console.log('SOP not found:', sopId);
            }
          } catch (error) {
            console.error('Error fetching SOP:', sopId, error);
          }
        }));
        console.log('Loaded SOPs:', sopsData);
        
        setSops(sopsData);
      } else {
        setSops({});
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
      toast({
        title: "Error",
        description: "Failed to load favorites",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.uid, categories]);

  useEffect(() => {
    console.log('Favorites useEffect triggered');
    if (user?.uid) {
      console.log('Loading favorites for user:', user.uid);
      loadFavorites();
    }
  }, [user?.uid, loadFavorites]);

  const handleCreateCategory = async () => {
    if (!user?.uid || !newCategoryName.trim()) return;

    try {
      await favoritesService.createCategory(
        newCategoryName,
        user.uid,
        displayName || 'Unknown User',
        profilePictureUrl || undefined
      );

      await refreshCategories();
      setNewCategoryName('');
      setIsCreatingCategory(false);
      
      toast({
        title: "Success",
        description: `Created category "${newCategoryName}"`,
      });
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: "Error",
        description: "Failed to create category",
        variant: "destructive",
      });
    }
  };

  const handleRemoveFavorite = async (sopToRemove: SOP) => {
    if (!user?.uid) return;

    try {
      // Remove SOP from all categories
      const updatedCategories = categories.map(category => ({
        ...category,
        sopIds: category.sopIds.filter(id => id !== sopToRemove.id),
        isPublic: category.isPublic || false
      }));

      for (const category of updatedCategories) {
        await updateDoc(doc(db, 'sopCategories', category.id), { sopIds: category.sopIds });
      }

      await refreshCategories();
      
      toast({
        title: "Success",
        description: "Removed from favorites",
      });
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast({
        title: "Error",
        description: "Failed to remove from favorites",
        variant: "destructive",
      });
    }
  };

  const handleMoveSOP = async (sopToMove: SOP, targetCategoryId: string) => {
    if (!user?.uid) return;

    try {
      // Remove SOP from all categories
      const updatedCategories = categories.map(category => ({
        ...category,
        sopIds: category.sopIds.filter(id => id !== sopToMove.id),
        isPublic: category.isPublic || false}));

      const targetCategory = updatedCategories.find(c => c.id === targetCategoryId);
      if (targetCategory) targetCategory.sopIds.push(sopToMove.id);

      // Update categories in Firestore
      for (const category of updatedCategories) {
        // Only update categories that changed
        if (category.id === targetCategoryId || 
            category.sopIds.length !== categories.find(c => c.id === category.id)?.sopIds.length) {
          await updateDoc(doc(db, 'sopCategories', category.id), {
            sopIds: category.sopIds
          });
        }
      }
      
      setIsMoveDialogOpen(false);
      await refreshCategories();
      setSelectedSOP(null);
      
      toast({
        title: "Success",
        description: "Moved to new category",
      });
    } catch (error) {
      console.error('Error moving SOP:', error);
      toast({
        title: "Error",
        description: "Failed to move SOP",
        variant: "destructive",
      });
    }
  };

  const handleVisibilityChange = async (categoryId: string, isPublic: boolean) => {
    if (!user?.uid) return;

    try {
      await favoritesService.updateCategoryVisibility(
        categoryId,
        isPublic,
        displayName || 'Unknown User',
        profilePictureUrl || undefined
      );

      await refreshCategories();
      
      toast({
        title: isPublic ? "Category is now public" : "Category is now private",
        description: isPublic 
          ? "Other users can now see and import this category" 
          : "This category is now only visible to you",
      });
    } catch (error) {
      console.error('Error updating category visibility:', error);
      toast({
        title: "Error",
        description: "Failed to update category visibility",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!user?.uid) return;

    try {
      await favoritesService.deleteCategory(categoryId);
      
      await refreshCategories();
      
      toast({
        title: "Category Deleted",
        description: "The category has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMessage = error instanceof Error && 
        error.message === 'Cannot delete the default "My Favorites" category'
          ? "The 'My Favorites' category cannot be deleted"
          : "Failed to delete category";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleUpdateCategoryName = async (categoryId: string, newName: string) => {
    if (!user?.uid) return;

    try {
      await favoritesService.updateCategoryName(categoryId, newName);
      await refreshCategories();
      // Toasting is handled in CollapsibleCategory, but can add a general one here if needed
      // toast({
      //   title: "Success",
      //   description: "Category name updated successfully.",
      // });
    } catch (error) {
      console.error('Error updating category name in Favorites page:', error);
      const errorMessage = error instanceof Error ? error.message : "An unknown error occurred.";
      toast({
        title: "Error",
        description: `Failed to update category name: ${errorMessage}`,
        variant: "destructive",
      });
    }
  };

  const handleCategoryImported = async () => {
    await refreshCategories();
    setActiveTab('my-favorites');
  };

  if (!user) {
    return (
      <div className="container mx-auto px-8 lg:px-16 py-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Please sign in to view your favorites.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-8 lg:px-16 py-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
        </div>
      </div>
    );
  }

  const filteredSOPs = Object.values(sops).filter(sop => {
    const query = searchQuery.toLowerCase();
    return (
      sop.title.toLowerCase().includes(query) ||
      sop.department.toLowerCase().includes(query) ||
      (sop.author1 && sop.author1.toLowerCase().includes(query)) ||
      (sop.author2 && sop.author2.toLowerCase().includes(query)) ||
      sop.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  });

  return (
    <div className="container mx-auto px-8 lg:px-16 py-8 min-h-screen">
      <div className="flex flex-col space-y-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">SOP Categories</h1>
        
        <Tabs defaultValue="my-favorites" value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center">
            <TabsList className="bg-transparent">
              <TabsTrigger value="my-favorites" className="data-[state=active]:shadow-none">My Favorites</TabsTrigger>
              <TabsTrigger value="public-categories">
                <Globe className="h-4 w-4 mr-2" />
                Public Categories
              </TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2">
              {activeTab === 'my-favorites' && (
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => setIsCreatingCategory(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Category
                </Button>
              )}
            </div>
          </div>
          
          <TabsContent value="my-favorites" className="mt-6">
            {/* Search */}
            {Object.keys(sops).length > 0 && (
              <Input
                type="text"
                placeholder="Search favorites..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 mb-6"
              />
            )}

            {/* Categories */}
            {categories
              .sort((a, b) => {
                // Always put "My Favorites" at the top
                if (a.name === 'My Favorites') return -1;
                if (b.name === 'My Favorites') return 1;
                return 0;
              })
              .map(category => {
              const categorySOPs = filteredSOPs.filter(sop => 
                category.sopIds.includes(sop.id)
              );

              if (categorySOPs.length === 0 && searchQuery) return null;

              return (
                <CollapsibleCategory
                  key={category.id}
                  category={category}
                  sops={categorySOPs}
                  userId={user.uid}
                  onVisibilityChange={(isPublic) => handleVisibilityChange(category.id, isPublic)}
                  onDeleteCategory={() => handleDeleteCategory(category.id)}
                  onMoveSOP={(sop) => {
                    setSelectedSOP(sop);
                    setIsMoveDialogOpen(true);
                  }}
                  onRemoveFavorite={handleRemoveFavorite}
                  onUpdateCategoryName={handleUpdateCategoryName} // Added prop
                />
              );
            })}

            {/* Empty State */}
            {Object.keys(sops).length === 0 && (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                You haven't added any SOPs to your favorites yet.
              </div>
            )}
          </TabsContent>

          <TabsContent value="public-categories" className="mt-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Public Categories</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Browse and import categories that other users have made public.
              </p>
            </div>
            <PublicCategoriesList onCategoryImported={handleCategoryImported} />
          </TabsContent>
        </Tabs>

        {/* Create Category Dialog */}
        <Dialog open={isCreatingCategory} onOpenChange={setIsCreatingCategory}>
          <DialogContent className="bg-white dark:bg-gray-900" aria-describedby="create-category-description">
            <DialogHeader>
              <div id="create-category-description" className="sr-only">Enter a name for your new category to organize your favorite SOPs.</div>
              <DialogTitle className="text-gray-900 dark:text-white">Create New Category</DialogTitle>
              <p id="create-category-description" className="text-gray-600 dark:text-gray-400 text-sm">
                Enter a name for your new category to organize your favorite SOPs.
              </p>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Input
                placeholder="Category Name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateCategory();
                  }
                }}
                className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              <div className="flex gap-2">
                <Button 
                  onClick={handleCreateCategory} 
                  className="flex-1 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white"
                >
                  Create
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsCreatingCategory(false)}
                  className="flex-1 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Move SOP Dialog */}
        <Dialog open={isMoveDialogOpen} onOpenChange={setIsMoveDialogOpen}>
          <DialogContent className="bg-white dark:bg-gray-900" aria-describedby="move-sop-description">
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">Move to Category</DialogTitle>
              <p id="move-sop-description" className="text-gray-600 dark:text-gray-400 text-sm">
                Select a category to move this SOP to.
              </p>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => selectedSOP && handleMoveSOP(selectedSOP, category.id)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-md flex items-center justify-between"
                >
                  <span className="text-gray-900 dark:text-gray-100">{category.name}</span>
                  <MoveRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default FavoriteSOPs;
